import { useNavigationManager } from '@Router/useNavigationManager';
import { useFormHook } from '@Hooks/index';
import { useSetError } from '@Hooks/index';
import { OnboardingFormFields } from '@Types/index';
import { useFormState } from 'react-hook-form';
import { useUpdatePrimaryOwnerPercentage } from '../api/ownersCenter.api';
import { UboResponse, useBoundStore } from '@Store/index';
import { ownersCenterScheme } from '../OwnersCenterForm/ownersCenterScheme';
import { RoutesMap } from '@Router/router.types.ts';
import { OwnersCenterFormType } from '../OwnersCenterForm/OwnersCenterForm.types';
import { useAddOwnerAnalytics } from '@Pages/happyFlowPages/ownersCenter/OwnersCenterAnalytics.tsx';
import { POATypes, uploadGenericSnapshot } from '@Services/api/uploadSnapshot';

export const useOwnersCenterLogic = (businessOwners: UboResponse[]) => {
  const { reportActOnUboCenter } = useAddOwnerAnalytics();
  const {
    customerData: { customerBusinessData },
  } = useBoundStore((state) => state.customer);
  const { getCustomerData, setCustomerData } = useBoundStore((state) => state);
  const { navigateToNextPage, navigateToSpecificPage } = useNavigationManager();
  const { mutateAsync: updatePrimaryOwnerPercentage, isPending: isUpdatePrimaryOwnerPercentage } =
    useUpdatePrimaryOwnerPercentage();

  const defaultValues: OwnersCenterFormType = {
    [OnboardingFormFields.OWNER_PERCENTAGE]: customerBusinessData.ownershipPercent ?? 50,
    [OnboardingFormFields.OWNER_CENTER_MULTI_OWNER_CONSENT]: false,
    [OnboardingFormFields.OWNER_CENTER_CONSENT_ONLY_UBO]: false,
    [OnboardingFormFields.OWNER_CENTER_OWNER_LIST]: businessOwners || [],
  };

  const formMethods = useFormHook({
    formSchema: ownersCenterScheme,
    formDefaultValues: defaultValues,
    schemaTitle: 'owners-center-form',
  });

  const { isValid } = useFormState({ control: formMethods.control });

  const updateMainOwnerPercentage = async () => {
    const customerDataToUpdate = getCustomerData();
    const newPercentage = formMethods.getValues()[OnboardingFormFields.OWNER_PERCENTAGE] || 0;
    await updatePrimaryOwnerPercentage(newPercentage);
    customerDataToUpdate.customerData.customerBusinessData.ownershipPercent = newPercentage;
    setCustomerData(customerDataToUpdate);
  };

  const handleSubmit = async () => {
    reportActOnUboCenter(
      formMethods.getValues()[OnboardingFormFields.OWNER_CENTER_OWNER_LIST].length
    );
    await updateMainOwnerPercentage();
     uploadGenericSnapshot(POATypes.OWNERS_CENTER);
    navigateToNextPage();
  };

  const onEditClick = async (uboId: string) => {
    await updateMainOwnerPercentage();
    navigateToSpecificPage(RoutesMap.ADD_OWNER, { uboId });
  };
  const onAddClick = async () => {
    await updateMainOwnerPercentage();
    navigateToSpecificPage(RoutesMap.ADD_OWNER);
  };

  useSetError({ formMethods });

  return {
    formMethods,
    handleSubmit,
    isValid,
    businessOwners,
    isUpdatePrimaryOwnerPercentage,
    onEditClick,
    onAddClick,
  };
};
